/* Line Property v3 (Onddoka custom / 2026-09)
 * 対象: .lp-field[data-lp-*]（snippets/line-property-block.liquid が出力）
 * 機能: 必須バリデーション(日本語インライン表示) / 大文字・英字制限 / 文字数カウンター /
 *       電話番号整形 / 刻印ルール連動(文字数閾値でデザイン自動切替) / 刻印プレビュー(SVG) /
 *       チェックボックスグループ集約 / ファイル添付UI
 */
(function () {
  'use strict';
  if (window.__lpV3Loaded) return;
  window.__lpV3Loaded = true;

  const CURVE_WORDS = ['カーブ', 'ラウンド', 'curve', 'round'];
  const isCurveOption = (text) => CURVE_WORDS.some((w) => (text || '').toLowerCase().includes(w.toLowerCase()));

  /* 電話番号の整形ルール（お客様の入力を尊重する方針 / 2026-09-04）
   * 1) 全角の数字・ハイフン・プラスを半角化し、空白は除去
   * 2) すでにハイフンが含まれていれば、その位置をそのまま採用（付け直さない）
   * 3) ハイフン無しで日本の携帯・IP電話（070/080/090/050 の11桁）だけ 3-4-4 に自動整形
   * 4) それ以外（固定電話・海外番号）は整形せず入力どおりに保持
   */
  function toHalfWidth(str) {
    return (str || '')
      .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
      .replace(/[－ー―‐−‑]/g, '-')
      .replace(/＋/g, '+')
      .replace(/[（）()]/g, '-');
  }
  function formatJpPhone(raw) {
    let v = toHalfWidth(raw).replace(/[\s\u3000]+/g, '');
    v = v.replace(/-{2,}/g, '-').replace(/^-+|-+$/g, '');
    if (!v) return '';
    if (v.includes('-')) return v;                 // 入力済みハイフンを尊重
    if (/^0[5789]0\d{8}$/.test(v)) {              // 携帯・IP電話（11桁）のみ自動整形
      return v.replace(/^(\d{3})(\d{4})(\d{4})$/, '$1-$2-$3');
    }
    return v;                                      // 固定電話・海外はそのまま
  }

  function setError(field, message) {
    const err = field.querySelector('.lp-error');
    if (!err) return;
    if (message) {
      err.textContent = message;
      err.hidden = false;
      field.classList.add('lp-invalid');
    } else {
      err.textContent = '';
      err.hidden = true;
      field.classList.remove('lp-invalid');
    }
  }

  function setNote(field, message) {
    const note = field.querySelector('.lp-note');
    if (!note) return;
    note.textContent = message || '';
    note.hidden = !message;
  }

  function control(field) {
    return field.querySelector('input:not([type=hidden]):not([type=checkbox]):not([type=radio]), select, textarea');
  }

  /* ---------- 必須バリデーション ---------- */
  let focusPending = false;
  function bindValidation(field) {
    const el = control(field);
    if (!el) return;
    const requiredMsg = field.dataset.lpRequiredMessage || 'この項目は必須です';
    el.addEventListener('invalid', (evt) => {
      evt.preventDefault(); // ブラウザ標準の吹き出しを抑止して自前表示
      let msg = requiredMsg;
      if (!el.validity.valueMissing) {
        if (el.validity.patternMismatch) msg = field.dataset.lpPatternMessage || '入力形式をご確認ください';
        else if (el.validity.typeMismatch) msg = '入力形式をご確認ください';
        else msg = el.validationMessage || msg;
      }
      setError(field, msg);
      if (!focusPending) {
        focusPending = true;
        setTimeout(() => {
          focusPending = false;
          const first = document.querySelector('.lp-field.lp-invalid');
          if (first) {
            first.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const c = control(first);
            if (c && c.type !== 'file') c.focus({ preventScroll: true });
          }
        }, 0);
      }
    });
    const clear = () => { if (el.checkValidity()) setError(field, ''); };
    el.addEventListener('input', clear);
    el.addEventListener('change', clear);
  }

  /* ---------- テキスト（大文字/英字/カウンター/電話） ---------- */
  function bindText(field) {
    const input = control(field);
    if (!input) return;
    const upper = field.dataset.lpUpper === 'true';
    const alpha = field.dataset.lpAlpha === 'true';
    const isTel = input.type === 'tel';
    const counter = field.querySelector('.lp-counter__now');

    if (alpha) {
      input.setAttribute('pattern', upper ? '[A-Z]*' : '[A-Za-z]*');
      field.dataset.lpPatternMessage = upper ? 'アルファベット大文字のみ入力できます' : 'アルファベットのみ入力できます';
    }
    if (isTel) {
      input.setAttribute('pattern', '\\+?[0-9][0-9\\-]{6,19}');
      field.dataset.lpPatternMessage = '電話番号は数字とハイフンで入力してください';
    }

    const onInput = () => {
      let v = input.value;
      let next = v;
      if (alpha) next = next.replace(/[^A-Za-z]/g, '');
      if (upper) next = next.toUpperCase();
      if (next !== v) {
        const pos = input.selectionStart;
        input.value = next;
        try { input.setSelectionRange(pos, pos); } catch (e) { /* noop */ }
      }
      if (counter) counter.textContent = String(input.value.length);
      document.dispatchEvent(new CustomEvent('lp:change', { detail: { field, input } }));
    };
    input.addEventListener('input', onInput);
    if (isTel) {
      input.addEventListener('blur', () => {
        const f = formatJpPhone(input.value);
        if (f !== input.value) { input.value = f; onInput(); }
      });
    } else {
      input.addEventListener('blur', () => {
        const t = input.value.trim();
        if (t !== input.value) { input.value = t; onInput(); }
      });
    }
    onInput();
  }

  /* ---------- 刻印ルール連動 + プレビュー ---------- */
  function scopeOf(field) {
    return field.closest('product-info, .product__info-container, form, body') || document;
  }
  function bindNameRole(field) {
    if (field.dataset.lpRole !== 'name') return;
    const input = control(field);
    if (!input) return;
    const threshold = parseInt(field.dataset.lpCurveThreshold || '0', 10);
    const showPreview = field.dataset.lpPreview === 'true';
    const scope = scopeOf(field);
    const designField = scope.querySelector('.lp-field[data-lp-role="design"]');
    const designSelect = designField ? control(designField) : null;

    const lineOptions = designSelect ? Array.from(designSelect.options).filter((o) => o.value && !isCurveOption(o.text)) : [];
    const curveOption = designSelect ? Array.from(designSelect.options).find((o) => o.value && isCurveOption(o.text)) : null;

    const preview = field.querySelector('.lp-preview');
    const lineText = preview ? preview.querySelector('.lp-preview__text--line') : null;
    const curveText = preview ? preview.querySelector('.lp-preview__text--curve') : null;
    const curvePath = preview ? preview.querySelector('textPath') : null;

    function currentIsCurve() {
      if (!designSelect) return false;
      const opt = designSelect.selectedOptions[0];
      return !!(opt && isCurveOption(opt.text));
    }

    function sync() {
      const value = input.value;
      const len = value.length;
      const over = threshold > 0 && len > threshold;

      if (designSelect && curveOption) {
        if (over) {
          if (!currentIsCurve()) {
            designSelect.value = curveOption.value;
            designSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
          lineOptions.forEach((o) => { o.disabled = true; });
          const msg = field.dataset.lpCurveMessage || `${threshold}文字を超えるため「${curveOption.text}」デザインになります`;
          setNote(designField, msg);
        } else {
          lineOptions.forEach((o) => { o.disabled = false; });
          setNote(designField, '');
        }
      }

      if (showPreview && lineText && curveText && curvePath) {
        const text = value || '';
        const curve = currentIsCurve() || over;
        lineText.textContent = curve ? '' : text;
        curvePath.textContent = curve ? text : '';
        lineText.hidden = curve;
        curveText.hidden = !curve;
        // 実物の刻印は文字数に関わらず同じ大きさ → プレビューも固定サイズ（横一列/カーブ共通）
        const size = 21;
        lineText.setAttribute('font-size', size);
        curveText.setAttribute('font-size', size);
        preview.classList.toggle('lp-preview--empty', !text);
      }
    }
    input.addEventListener('input', sync);
    if (designSelect) designSelect.addEventListener('change', sync);
    sync();
  }

  /* ---------- チェックボックスグループ ---------- */
  function bindCheckGroup(field) {
    const hidden = field.querySelector('.lp-check-group__hidden');
    const boxes = field.querySelectorAll('.lp-check-group__input');
    if (!hidden) return;
    const fill = () => {
      hidden.value = Array.from(boxes).filter((b) => b.checked).map((b) => b.value).join(', ');
    };
    boxes.forEach((b) => b.addEventListener('change', fill));
    fill();
  }

  /* ---------- ファイル ---------- */
  function bindFile(field) {
    const input = field.querySelector('input[type=file]');
    const label = field.querySelector('.lp-file__label');
    const remove = field.querySelector('.lp-file__remove');
    const icon = field.querySelector('.lp-file__icon');
    if (!input || !label) return;
    const title = field.dataset.lpTitle || label.textContent;
    const render = () => {
      const has = input.files && input.files.length > 0;
      label.textContent = has ? input.files[0].name : title;
      if (remove) remove.style.display = has ? 'inline-block' : 'none';
      if (icon) icon.style.display = has ? 'none' : 'inline-block';
    };
    input.addEventListener('change', render);
    if (remove) remove.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); input.value = ''; render(); });
    render();
  }

  /* ---------- 送信ガード ----------
   * Dawn の商品フォームは novalidate 付きのため、ブラウザ標準の必須チェックが走らない。
   * submit を capture 段階で捕捉し、lp-field を含むフォームが無効なら送信を止めて reportValidity() で invalid を発火させる。
   */
  document.addEventListener('submit', (evt) => {
    const form = evt.target;
    if (!(form instanceof HTMLFormElement)) return;
    const hasLp = Array.from(form.elements).some((el) => el.closest && el.closest('.lp-field'));
    if (!hasLp) return;
    if (form.checkValidity()) return;
    evt.preventDefault();
    evt.stopImmediatePropagation();
    form.reportValidity();
  }, true);

  /* ---------- init ---------- */
  function init(root) {
    (root || document).querySelectorAll('.lp-field:not([data-lp-ready])').forEach((field) => {
      field.dataset.lpReady = '1';
      const type = field.dataset.lpType;
      if (type === 'text') bindText(field);
      if (type === 'text' || type === 'textarea' || type === 'select' || type === 'file') bindValidation(field);
      if (type === 'check_group') bindCheckGroup(field);
      if (type === 'file') bindFile(field);
    });
    // role 連動は design セレクトの初期化後に
    (root || document).querySelectorAll('.lp-field[data-lp-role="name"]:not([data-lp-role-ready])').forEach((field) => {
      field.dataset.lpRoleReady = '1';
      bindNameRole(field);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  // テーマエディタでのブロック再描画 / セクション再読込に追従
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => init(e.target));
})();
